import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Copy, Download, Save, Trash2 } from "lucide-react";

export default function CampaignDetailPage() {
  const { id } = useParams();
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [jsonEdit, setJsonEdit] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios
      .get(`${API}/campaigns/${id}`, { headers })
      .then((r) => {
        setCampaign(r.data);
        setJsonEdit(JSON.stringify(r.data.campaign_data, null, 2));
      })
      .catch(() => {
        toast.error("Campaign not found");
        navigate("/");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id]);

  const saveCampaign = async () => {
    setSaving(true);
    try {
      const parsed = JSON.parse(jsonEdit);
      await axios.put(`${API}/campaigns/${id}`, { campaign_data: parsed }, { headers });
      setCampaign((prev) => ({ ...prev, campaign_data: parsed }));
      setEditMode(false);
      toast.success("Campaign updated");
    } catch (err) {
      toast.error(err instanceof SyntaxError ? "Invalid JSON" : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteCampaign = async () => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, { headers });
      toast.success("Campaign deleted");
      navigate("/");
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyJson = async () => {
    const text = editMode ? jsonEdit : JSON.stringify(campaign.campaign_data, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("JSON copied to clipboard");
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success("JSON copied to clipboard");
    }
  };

  const downloadJson = () => {
    const text = editMode ? jsonEdit : JSON.stringify(campaign.campaign_data, null, 2);
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${campaign?.campaign_data?.campaign_name || "campaign"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const syntaxHighlight = (json) => {
    if (!json) return "";
    return json
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(
        /("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
          let cls = "json-number";
          if (/^"/.test(match)) { cls = /:$/.test(match) ? "json-key" : "json-string"; }
          else if (/true|false/.test(match)) { cls = "json-boolean"; }
          else if (/null/.test(match)) { cls = "json-null"; }
          return `<span class="${cls}">${match}</span>`;
        }
      );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex gap-2">
          <div className="ai-dot dot-bounce-1" />
          <div className="ai-dot dot-bounce-2" />
          <div className="ai-dot dot-bounce-3" />
        </div>
      </div>
    );
  }

  if (!campaign) return null;
  const cd = campaign.campaign_data || {};

  return (
    <div data-testid="campaign-detail-page">
      <button
        onClick={() => navigate("/")}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6 transition-colors"
        data-testid="back-to-dashboard-detail"
      >
        <ArrowLeft size={14} /> Back to Dashboard
      </button>

      <div className="animate-in" style={{ animationDelay: "0.05s" }}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] mb-1">Campaign Detail</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              {cd.campaign_name || "Untitled"}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              {cd.type && <span className="tag-chip">{cd.type}</span>}
              {cd.status && <span className="tag-chip">{cd.status}</span>}
              <span className="text-xs text-muted-foreground">
                Created {new Date(campaign.created_at).toISOString().split("T")[0]}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={copyJson} data-testid="detail-copy-btn" className="p-2 text-muted-foreground hover:text-foreground border border-border rounded-sm transition-colors" title="Copy JSON"><Copy size={14} /></button>
            <button onClick={downloadJson} data-testid="detail-download-btn" className="p-2 text-muted-foreground hover:text-foreground border border-border rounded-sm transition-colors" title="Download JSON"><Download size={14} /></button>
            <Button onClick={() => (editMode ? saveCampaign() : setEditMode(true))} disabled={saving} data-testid="detail-edit-save-btn" className="bg-primary hover:bg-primary/85 text-primary-foreground rounded-sm h-9 px-4 text-sm">
              <Save size={14} className="mr-1.5" />{editMode ? (saving ? "Saving..." : "Save") : "Edit JSON"}
            </Button>
            <button onClick={deleteCampaign} data-testid="detail-delete-btn" className="p-2 text-destructive hover:bg-destructive/10 border border-border rounded-sm transition-colors" title="Delete"><Trash2 size={14} /></button>
          </div>
        </div>

        {campaign.brief && (
          <div className="bg-card border border-border p-5 rounded-sm mb-6">
            <p className="text-xs text-muted-foreground uppercase tracking-[0.15em] mb-2">Original Brief</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{campaign.brief}</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Target", value: cd.total_target },
            { label: "Budget", value: cd.total_budget ? `$${cd.total_budget}` : "—" },
            { label: "Start", value: cd.start_date || "—" },
            { label: "End", value: cd.end_date || "—" },
          ].map((item) => (
            <div key={item.label} className="bg-card border border-border p-4 rounded-sm">
              <p className="text-xs text-muted-foreground uppercase tracking-[0.15em]">{item.label}</p>
              <p className="text-lg font-bold text-foreground mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>{item.value || "—"}</p>
            </div>
          ))}
        </div>

        {cd.specifications && (
          <div className="bg-card border border-border p-5 rounded-sm mb-6">
            <p className="text-xs text-muted-foreground uppercase tracking-[0.15em] mb-3">Specifications</p>
            <div className="space-y-3">
              {[
                { label: "Job Titles", value: cd.specifications.job_title_keywords },
                { label: "Industries", value: cd.specifications.industry_types },
                { label: "Employee Size", value: cd.specifications.employee_size },
                { label: "Revenue", value: cd.specifications.revenue_size },
                { label: "Geography", value: cd.specifications.geo },
                { label: "Functions", value: cd.specifications.job_functions },
                { label: "Levels", value: cd.specifications.job_levels },
                { label: "Lead Types", value: cd.specifications.lead_types },
              ].map((field) => field.value && (
                <div key={field.label}>
                  <span className="text-xs text-muted-foreground mr-2">{field.label}:</span>
                  {Array.isArray(field.value) ? (
                    <span className="flex flex-wrap gap-1 mt-1">{field.value.map((v, i) => (<span key={i} className="tag-chip">{v}</span>))}</span>
                  ) : (<span className="text-sm text-muted-foreground">{field.value}</span>)}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-[0.15em]">Campaign JSON</p>
            {editMode && (
              <button onClick={() => { setEditMode(false); setJsonEdit(JSON.stringify(campaign.campaign_data, null, 2)); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Cancel Edit</button>
            )}
          </div>
          {editMode ? (
            <Textarea value={jsonEdit} onChange={(e) => setJsonEdit(e.target.value)} className="json-viewer font-mono text-sm bg-card border-border text-foreground min-h-[400px] p-6 rounded-sm" data-testid="json-edit-textarea" />
          ) : (
            <div className="json-viewer max-h-[600px] overflow-auto" data-testid="json-detail-viewer" dangerouslySetInnerHTML={{ __html: syntaxHighlight(JSON.stringify(cd, null, 2)) }} />
          )}
        </div>
      </div>
    </div>
  );
}
